import { useEffect } from 'react';

import { normalizeDocument } from '@eduzz-registry-360/validators';
import { useQuery } from '@tanstack/react-query';

import { getAccountsToken, logout } from '@/lib/accounts';
import { trpc } from '@/lib/trpc';
import useAuthStore from '@/stores/auth';
import useRegistryStore, { initialRegistry, registryStore } from '@/stores/registry';

const DOCUMENT_INACTIVE_ERROR = 'document_inactive';
const USER_ALREADY_REGISTERED_ERROR = 'user_already_registered';

const clearedBankOperationalFields = {
  bank: initialRegistry.bank,
  agency: initialRegistry.agency,
  agencyDigit: initialRegistry.agencyDigit,
  account: initialRegistry.account,
  accountDigit: initialRegistry.accountDigit,
  accountType: initialRegistry.accountType
};

const isDocumentInactiveError = (error: unknown): boolean =>
  Boolean(error && typeof error === 'object' && 'message' in error && error.message === DOCUMENT_INACTIVE_ERROR);

const isUserAlreadyRegisteredError = (error: unknown): boolean =>
  Boolean(error && typeof error === 'object' && 'message' in error && error.message === USER_ALREADY_REGISTERED_ERROR);

export const useAccounts = () => {
  const isAuthenticated = useAuthStore(({ isAuthenticated }) => isAuthenticated());
  const authToken = useAuthStore(({ authToken }) => authToken);
  const { setAuthData, setAuthCompleted } = useAuthStore(({ setAuthData, setAuthCompleted }) => ({
    setAuthData,
    setAuthCompleted
  }));

  const { setRegistryFields } = useRegistryStore(({ setFields }) => ({ setRegistryFields: setFields }));

  useEffect(() => {
    if (authToken) {
      setAuthCompleted();
    }
  }, [authToken, setAuthCompleted]);

  const { data: accountsCode } = useQuery({
    queryKey: ['accounts', 'token'],
    queryFn: getAccountsToken,
    enabled: !authToken
  });

  const {
    data: authData,
    refetch,
    error
  } = trpc.auth.login.useQuery({ token: accountsCode! }, { enabled: !!accountsCode && !authToken });

  useEffect(() => {
    if (!authData) {
      return;
    }

    if (!authData.user.eduzzId) {
      logout();
      return;
    }

    setAuthData({
      ...authData,
      user: {
        ...authData.user,
        eduzzId: authData.user.eduzzId!
      }
    });

    const rawDocument = authData.user.document ? normalizeDocument(authData.user.document) : '';
    const isPessoaFisica = rawDocument.length === 11;

    let bankAccountOwner = authData.user.name;

    if (!isPessoaFisica && authData.user.companyName) {
      bankAccountOwner = authData.user.companyName;
    }

    const previousDocument = normalizeDocument(registryStore.getState().registry.document);
    const documentChanged = Boolean(previousDocument && rawDocument && previousDocument !== rawDocument);

    setRegistryFields({
      fullName: authData.user.name,
      document: rawDocument,
      bankAccountOwner,
      bankAccountDocument: rawDocument,
      bankAccountType: isPessoaFisica ? 'personal' : 'company',
      personType: isPessoaFisica ? 'F' : 'J',
      companyType: authData.user.companyType ?? '',
      ...(documentChanged ? clearedBankOperationalFields : {})
    });

    setAuthCompleted();
  }, [authData, setAuthCompleted, setAuthData, setRegistryFields]);

  const documentInactive = isDocumentInactiveError(error);
  const userAlreadyRegistered = isUserAlreadyRegisteredError(error);

  useEffect(() => {
    if (!error || documentInactive || userAlreadyRegistered) {
      return;
    }

    logout();
  }, [error, documentInactive, userAlreadyRegistered]);

  return {
    refetch,
    error,
    documentInactive,
    userAlreadyRegistered,
    isAuthenticated
  };
};
